import { useMemo, useState } from 'react'
import { Button } from '../../../../components'
import { PageSkeleton } from '../../../../components/Skeleton'
import { getApiErrorMessage } from '../../../../shared/utils/errorUtils'
import { useLeavePolicies } from '../hooks/useLeavePolicies'
import type { LeavePolicyDto } from '../types/hrmsLeavePolicies.types'
import { AddLeavePolicyDrawer } from './AddLeavePolicyDrawer'
import { LeavePoliciesHeaderCard } from './LeavePoliciesHeaderCard'
import { LeavePoliciesTable } from './LeavePoliciesTable'
import { LeavePolicyFilters } from './LeavePolicyFilters'

interface LeavePoliciesTabProps {
  onAddLeavePolicy?: () => void
  onEditLeavePolicy?: (id: string) => void
}

export function LeavePoliciesTab({
  onAddLeavePolicy,
  onEditLeavePolicy,
}: LeavePoliciesTabProps) {
  const {
    isLoading,
    isError,
    error,
    refetch,
    policies,
    searchQuery,
    setSearchQuery,
    employeeGroupFilter,
    setEmployeeGroupFilter,
    statusFilter,
    setStatusFilter,
    filteredPolicies,
    totalAssignedPersonnel,
    handleClearFilters,
    staffTypeOptions,
    isStaffTypesLoading,
  } = useLeavePolicies()

  const filterEmployeeGroupOptions = useMemo(() => {
    return [{ label: 'All', value: 'All' }, ...staffTypeOptions]
  }, [staffTypeOptions])

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null)

  const handleOpenAdd = () => {
    setEditingPolicyId(null)
    setDrawerOpen(true)
    onAddLeavePolicy?.()
  }

  const handleOpenEdit = (id: string) => {
    setEditingPolicyId(id)
    setDrawerOpen(true)
    onEditLeavePolicy?.(id)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setEditingPolicyId(null)
  }

  if (isLoading && policies.length === 0) {
    return <PageSkeleton />
  }

  if (isError) {
    return (
      <div className="w-full p-6 font-sans">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800 space-y-3">
          <h3 className="text-base font-bold">Failed to load leave policies</h3>
          <p className="text-sm text-red-600">
            {getApiErrorMessage(error, 'An error occurred while fetching leave policies.')}
          </p>
          <Button variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5 min-w-0">
      <LeavePoliciesHeaderCard onAddLeavePolicy={handleOpenAdd} />

      <LeavePolicyFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        employeeGroupFilter={employeeGroupFilter}
        onEmployeeGroupFilterChange={setEmployeeGroupFilter}
        employeeGroupOptions={filterEmployeeGroupOptions}
        isEmployeeGroupLoading={isStaffTypesLoading}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onClearFilters={handleClearFilters}
      />

      <LeavePoliciesTable
        policies={filteredPolicies}
        totalAssignedPersonnel={totalAssignedPersonnel}
        onEditLeavePolicy={handleOpenEdit}
      />

      <AddLeavePolicyDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        mode={editingPolicyId ? 'edit' : 'create'}
        policyId={editingPolicyId}
        existingPolicies={policies}
      />
    </div>
  )
}
